import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { Command } from "cmdk";
import { ArrowRightLeft, Building2, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { Loan, LoansResponse, Me } from "../lib/types";
import { moneyCompact } from "../lib/format";
import { dateShort, todayIn } from "../lib/dates";
import { setDensity, setHelpLevel, setTheme, densityStore, HELP_LEVEL_OPTIONS, helpLevelStore, useIsDark } from "../lib/prefs";
import {
  paletteWorkspace,
  prospectingNav,
  scenariosNav,
  useCrmSearch,
  workspacesFor,
  type Workspace,
} from "../lib/crm";
import { navItems, type NavItem } from "./AppShell";
import { cx, StagePill } from "./ui";

const SWITCH: Record<Workspace, { label: string; to: string; keywords: string }> = {
  prospecting: { label: "Prospecting", to: "/prospecting", keywords: "prospecting crm directory relationships wall" },
  scenarios: { label: "Scenarios", to: "/scenarios", keywords: "scenarios desk lo linked uuid" },
  deals: { label: "Active deals", to: "/pipeline", keywords: "active deals pipeline loans wall" },
};

export function CommandPalette({
  me,
  open,
  onOpenChange,
  onOpenLoan,
}: {
  me: Me;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onOpenLoan: (id: string) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dark = useIsDark();
  const density = densityStore.use();
  const helpLevel = helpLevelStore.use();
  const today = todayIn(me.settings.timezone);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const spaces = workspacesFor(me);
  const ws = paletteWorkspace(me, pathname);
  // Scenarios and Active deals are both deal-side: loans are searchable from either.
  const isProspecting = ws === "prospecting";
  const dealNav: NavItem[] =
    ws === "scenarios"
      ? scenariosNav(me)
      : navItems(me);

  const crmRes = useCrmSearch(
    open && isProspecting ? search : "",
    50,
    open && isProspecting,
  );

  const loans = useMemo(() => {
    if (!open || isProspecting) return [] as Loan[];
    const m = new Map<string, Loan>();
    for (const q of qc.getQueryCache().findAll({ queryKey: ["loans"] }))
      for (const l of (q.state.data as LoansResponse | undefined)?.loans ?? [])
        m.set(l.id, l);
    return [...m.values()];
  }, [open, isProspecting, qc]);

  const run = (fn: () => void) => {
    onOpenChange(false);
    fn();
  };
  const itemCls =
    "flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[14px] text-ink data-[selected=true]:bg-selected";
  const groupCls =
    "[&_[cmdk-group-heading]]:eyebrow [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-ink-3";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-navy-950/40" />
        <Dialog.Content data-feedback-loan-list="true"
          className={cx(
            "fixed top-[12vh] left-1/2 z-50 w-[min(640px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-[10px] border bg-raised shadow-[var(--shadow-3)]",
            isProspecting ? "border-green-700" : "border-line",
          )}
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <div
            className={cx(
              "flex items-center justify-between px-4 py-1.5 text-[11px] font-semibold tracking-wider text-white uppercase",
              isProspecting ? "bg-green-900" : "bg-nav",
            )}
          >
            <span>
              {isProspecting
                ? "Prospecting · Directory & Relationships"
                : ws === "scenarios"
                  ? "Scenarios · LO-linked scenarios"
                  : "Active deals · Live Pipeline"}
            </span>
            <span className="text-white/75">
              {isProspecting ? "No borrower data" : "Borrower workspace"}
            </span>
          </div>
          <Command label="Command palette" loop shouldFilter={!isProspecting}>
            <Command.Input
              autoFocus
              value={search}
              onValueChange={setSearch}
              placeholder={
                isProspecting
                  ? "Search loan officers, brokerages, NMLS, email… or jump anywhere"
                  : "Find a loan by name, loan #, broker… or type a command"
              }
              className="h-12 w-full border-b border-subtle bg-transparent px-4 text-[15px] text-ink outline-none placeholder:text-ink-3"
            />
            <Command.List data-feedback-loan-list="true" className="max-h-[60vh] overflow-y-auto p-1.5">
              <Command.Empty className="px-3 py-6 text-center text-ink-3">
                No results on this side of the wall.
              </Command.Empty>

              {isProspecting ? (
                <>
                  {(crmRes.data?.people.length ?? 0) > 0 ? (
                    <Command.Group heading="Loan officers" className={groupCls}>
                      {(crmRes.data?.people ?? []).slice(0, 12).map((p) => (
                        <Command.Item
                          key={p.id}
                          value={`lo ${p.full_name} ${p.nmls ?? ""} ${p.email ?? ""} ${p.company_name ?? ""} ${p.id}`}
                          onSelect={() =>
                            run(() => navigate(`/prospecting/people/${p.id}`))
                          }
                          className={itemCls}
                        >
                          <UserRound
                            size={15}
                            className="shrink-0 text-ink-3"
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">
                              {p.full_name}
                            </span>
                            <span className="block truncate text-[12px] text-ink-3 tnum">
                              {[
                                p.company_name,
                                p.nmls ? `NMLS ${p.nmls}` : null,
                                p.email,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                            </span>
                          </span>
                          <span className="text-[12px] text-ink-3">
                            {p.owner_name
                              ? `AE: ${p.owner_name}`
                              : "Unassigned"}
                          </span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ) : null}

                  {(crmRes.data?.companies.length ?? 0) > 0 ? (
                    <Command.Group
                      heading="Brokerages & companies"
                      className={groupCls}
                    >
                      {(crmRes.data?.companies ?? []).slice(0, 8).map((c) => (
                        <Command.Item
                          key={c.id}
                          value={`company ${c.name} ${c.nmls ?? ""} ${c.id}`}
                          onSelect={() =>
                            run(() =>
                              navigate(`/prospecting/companies/${c.id}`),
                            )
                          }
                          className={itemCls}
                        >
                          <Building2
                            size={15}
                            className="shrink-0 text-ink-3"
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">
                              {c.name}
                            </span>
                            <span className="block truncate text-[12px] text-ink-3 tnum">
                              {[
                                c.nmls ? `NMLS ${c.nmls}` : null,
                                `${c.lo_count} LOs`,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ) : null}

                  <Command.Group
                    heading="Go to (Prospecting)"
                    className={groupCls}
                  >
                    {prospectingNav(me).map((n) => (
                      <Command.Item
                        key={n.to}
                        value={`go ${n.label}`}
                        onSelect={() => run(() => navigate(n.to))}
                        className={itemCls}
                      >
                        {n.label}
                      </Command.Item>
                    ))}
                  </Command.Group>
                </>
              ) : (
                <>
                  <Command.Group heading="Loans" className={groupCls}>
                    {loans.map((l) => (
                      <Command.Item
                        key={l.id} data-loan-id={l.id}
                        value={`${l.file_name} ${l.loan_number ?? ""} ${l.broker ?? ""} ${l.owner_name} ${l.id}`}
                        onSelect={() => run(() => onOpenLoan(l.id))}
                        className={itemCls}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            {l.file_name || "Untitled loan"}
                          </span>
                          <span className="block truncate text-[12px] text-ink-3 tnum">
                            {[
                              l.loan_number,
                              moneyCompact(l.amount),
                              l.coe_date
                                ? `COE ${dateShort(l.coe_date, today)}`
                                : null,
                              l.owner_name,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </span>
                        <StagePill
                          stage={me.stages.find((s) => s.key === l.stage_key)}
                          total={me.stages.length}
                          compact
                        />
                      </Command.Item>
                    ))}
                  </Command.Group>
                  <Command.Group
                    heading={ws === "scenarios" ? "Go to (Scenarios)" : "Go to (Active deals)"}
                    className={groupCls}
                  >
                    {dealNav.map((n) => (
                      <Command.Item
                        key={n.to}
                        value={`go ${n.label} ${n.hint ?? ""}`}
                        onSelect={() =>
                          run(() =>
                            n.reloadDocument
                              ? window.location.assign(n.to)
                              : navigate(n.to),
                          )
                        }
                        className={itemCls}
                      >
                        {n.label}
                      </Command.Item>
                    ))}
                  </Command.Group>
                </>
              )}

              {spaces.length > 1 ? (
                <Command.Group heading="Switch workspace" className={groupCls}>
                  {spaces
                    .filter((w) => w !== ws)
                    .map((w) => (
                      <Command.Item
                        key={w}
                        value={`switch ${SWITCH[w].keywords}`}
                        onSelect={() => run(() => navigate(SWITCH[w].to))}
                        className={itemCls}
                      >
                        <ArrowRightLeft
                          size={15}
                          className="shrink-0 text-ink-3"
                          aria-hidden
                        />
                        Switch to {SWITCH[w].label} workspace
                      </Command.Item>
                    ))}
                </Command.Group>
              ) : null}

              <Command.Group heading="Training & Help" className={groupCls}>
                <Command.Item
                  value="training manual cliff notes guide help modules"
                  onSelect={() => run(() => navigate("/training"))}
                  className={itemCls}
                >
                  Open Training Manual (Cliff Notes v1)
                </Command.Item>
                {HELP_LEVEL_OPTIONS.map((opt) => (
                  <Command.Item
                    key={opt.value}
                    value={`tooltip help level ${opt.value} ${opt.label}`}
                    onSelect={() => run(() => setHelpLevel(opt.value))}
                    className={itemCls}
                  >
                    <span className="flex-1">Tooltips & Help Level: {opt.label}</span>
                    {helpLevel === opt.value ? (
                      <span className="text-[11px] font-semibold text-accent">Active</span>
                    ) : null}
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Group heading="Settings" className={groupCls}>
                <Command.Item
                  value="toggle dark mode theme"
                  onSelect={() => run(() => setTheme(dark ? "light" : "dark"))}
                  className={itemCls}
                >
                  Switch to {dark ? "light" : "dark"} mode
                </Command.Item>
                <Command.Item
                  value="density compact comfortable"
                  onSelect={() =>
                    run(() =>
                      setDensity(
                        density === "compact" ? "comfortable" : "compact",
                      ),
                    )
                  }
                  className={itemCls}
                >
                  Density: {density === "compact" ? "comfortable" : "compact"}
                </Command.Item>
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
